import { PaginationState, type Table } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { Separator } from '../ui/separator'

interface DataTablePaginationProps<TData> {
  table: Table<TData>
  totalRecords?: number
  disableRowPerPage?: boolean
  pageSizeOptions?: number[]
  isLoading?: boolean
}

export function TablePagination<TData>({
  table,
  disableRowPerPage = false,
  totalRecords,
  pageSizeOptions = [10, 25, 40, 50],
  isLoading = false,
}: DataTablePaginationProps<TData>) {
  if (isLoading) return null
  return (
    <div className="flex w-full flex-col items-center justify-between gap-4 overflow-auto py-1 sm:flex-row sm:gap-8 bg-[#F5F6FA]">
      <div className="flex row items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8 w-full">
        {/*Item number of total item && Page of total */}
        {totalRecords ? (
          <div className="flex items-center justify-start text-sm gap-1 opacity-60 font-semibold w-full">
            {getTotalRecords(table.getState().pagination, totalRecords)}
          </div>
        ) : (
          <div className="flex items-center justify-start text-sm gap-1 opacity-60 font-semibold w-full">
            {/* Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()} */}
          </div>
        )}
        {/*Pagination button*/}
        <div className="h-9 border w-24 rounded-lg flex flex-row border-[#D5D5D5] bg-white">
          <Button
            aria-label="Go to previous page"
            variant="outline"
            className="size-8 p-0 w-full rounded-none bg-transparent !rounded-l-lg"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <svg
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.41 10.4064L2.83 5.99999L7.41 1.59359L6 0.23999L0 5.99999L6 11.76L7.41 10.4064Z"
                fill="#202224"
              />
            </svg>
          </Button>
          <Separator className="w-[1px] bg-[#D5D5D5] h-full" />
          <Button
            aria-label="Go to next page"
            variant="outline"
            className="size-8 p-0 w-full rounded-none bg-transparent !rounded-r-lg"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <svg
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.59 10.4064L5.17 5.99999L0.59 1.59359L2 0.23999L8 5.99999L2 11.76L0.59 10.4064Z"
                fill="#202224"
              />
            </svg>
          </Button>
        </div>
      </div>
    </div>
  )
}

// support calculate total records
const getTotalRecords = (
  pagination: PaginationState,
  totalRecords: number
): string => {
  const currentPage = pagination.pageIndex + 1
  const itemPerPage = pagination.pageSize
  const firstRecord = (currentPage - 1) * itemPerPage + 1
  const lastRecord = Math.min(currentPage * itemPerPage, totalRecords)
  return `Showing ${firstRecord}-${lastRecord} of ${totalRecords}`
}
